import snarkdown from 'snarkdown'

// Sanitizing markdown renderer for the legal-assist chat (Sprint T8).
//
// snarkdown converts markdown -> HTML but passes raw HTML through, so its
// output is NEVER trusted: it is parsed into an inert document (DOMParser
// does not execute scripts or load subresources) and rebuilt node-by-node
// against a strict allowlist. Everything else is dropped to its text
// content (auto-escaped on insertion). No raw HTML passthrough, no
// attributes except a vetted http(s) href.

const ALLOWED_TAGS = new Set([
  'P', 'EM', 'STRONG', 'UL', 'OL', 'LI',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'BLOCKQUOTE', 'CODE', 'PRE', 'HR', 'A', 'BR', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD'
])

const HTTP_URL = /^https?:\/\//i
// Same-origin docs guide links (cited by the Documentation Assistant).
// Strict path shape: /docs or /docs/<slug> — no protocol-relative //, no
// traversal, nothing else on the same origin is linkable.
const DOCS_PATH = /^\/docs(\/[a-z0-9-]+)?$/

const sanitizeInto = (source, target, doc) => {
  source.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      target.appendChild(doc.createTextNode(node.nodeValue))

      return
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return

    if (!ALLOWED_TAGS.has(node.tagName)) {
      // Disallowed element (script/img/iframe/...): keep only its text.
      if (node.textContent) target.appendChild(doc.createTextNode(node.textContent))

      return
    }

    const clean = doc.createElement(node.tagName)

    if (node.tagName === 'A') {
      const href = node.getAttribute('href') || ''

      if (HTTP_URL.test(href) || DOCS_PATH.test(href)) {
        clean.setAttribute('href', href)
        clean.setAttribute('target', '_blank')
        clean.setAttribute('rel', 'noopener noreferrer')
      }
    }

    sanitizeInto(node, clean, doc)
    target.appendChild(clean)
  })
}

// Returns a sanitized DocumentFragment for the given markdown string.

// GitHub-style pipe tables (snarkdown does not support them): a header row
// followed by a `| --- | --- |` separator row, then body rows. Converted to
// <table> HTML here (cell contents still go through snarkdown for inline
// emphasis), then sanitized like everything else — TABLE/THEAD/TBODY/TR/TH/TD
// are already in the allowlist.
const TABLE_SEP = /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/

const splitRow = (line) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim())

const tableToHtml = (lines) => {
  const cells = (arr, tag) => arr.map((c) => `<${tag}>${snarkdown(c)}</${tag}>`).join('')
  const header = splitRow(lines[0])
  const rows = lines.slice(2).map(splitRow)
  const body = rows.length ? `<tbody>${rows.map((r) => `<tr>${cells(r, 'td')}</tr>`).join('')}</tbody>` : ''

  return `<table><thead><tr>${cells(header, 'th')}</tr></thead>${body}</table>`
}

const renderBlock = (block) => {
  const lines = block.split('\n')
  const out = []
  let buf = []
  const flush = () => {
    if (buf.length) {
      out.push(snarkdown(buf.join('\n')))
      buf = []
    }
  }

  for (let i = 0; i < lines.length; i += 1) {
    if (lines[i].includes('|') && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1])) {
      let end = i + 2

      while (end < lines.length && lines[end].includes('|')) end += 1

      flush()
      out.push(tableToHtml(lines.slice(i, end)))
      i = end - 1
    } else {
      buf.push(lines[i])
    }
  }

  flush()

  return out.join('')
}

export default function renderMarkdown (markdown) {
  const text = (markdown || '').toString()

  // snarkdown treats single newlines as soft breaks inside one paragraph;
  // render paragraph blocks separately so lists/paragraphs keep structure.
  const html = text
    .split(/\n{2,}/)
    .map((block) => renderBlock(block))
    .filter((block) => block !== '')
    .map((block) => (/^<(ul|ol|h\d|blockquote|pre|hr|table)/i.test(block) ? block : `<p>${block}</p>`))
    .join('')

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const fragment = document.createDocumentFragment()

  sanitizeInto(doc.body, fragment, document)

  return fragment
}
